'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/hooks/use-i18n';

export interface StarRatingProps {
  value?: number;
  defaultValue?: number;
  max?: number;
  readOnly?: boolean;
  disabled?: boolean;
  className?: string;
  starLineClassName?: string;
  onChange?: (value: number) => void;
  size?: number;
  labels?: string[]; // 新增：每颗星下的标签
}

export const StarRating = React.forwardRef<HTMLDivElement, StarRatingProps>(
  (
    {
      value,
      defaultValue = 0,
      max = 5,
      readOnly = false,
      disabled = false,
      className,
      starLineClassName,
      onChange,
      size = 16,
      labels,
      ...props
    },
    ref,
  ) => {
    const { t } = useI18n('common');
    const defaultLabels = labels
      ? [
          t('common:rating.veryBad'),
          t('common:rating.bad'),
          t('common:rating.average'),
          t('common:rating.good'),
          t('common:rating.excellent'),
        ]
      : [];
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [hoverValue, setHoverValue] = React.useState<number | null>(null);
    const isControlled = value !== undefined;
    const stars = Array.from({ length: max }, (_, i) => i + 1);
    const displayValue =
      hoverValue !== null ? hoverValue : isControlled ? value! : internalValue;
    const showLabels =
      Array.isArray(defaultLabels) && defaultLabels.length === max;

    function handleClick(star: number) {
      if (readOnly || disabled) return;
      if (!isControlled) setInternalValue(star);
      onChange?.(star);
    }
    function handleMouseEnter(star: number) {
      if (readOnly || disabled) return;
      setHoverValue(star);
    }
    function handleMouseLeave() {
      if (readOnly || disabled) return;
      setHoverValue(null);
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col select-none',
          className,
          disabled && 'opacity-50 pointer-events-none',
        )}
        aria-disabled={disabled}
        aria-readonly={readOnly}
        {...props}
      >
        {/* 星星行 */}
        <div className={cn('flex items-end', starLineClassName)}>
          {stars.map((star, idx) => {
            const label = showLabels ? defaultLabels[idx] : '';
            const isActive = displayValue === star;
            return (
              <div
                key={star}
                className={cn(
                  'flex flex-col items-center',
                  `min-w-[${size * 3}px]`,
                  idx !== stars.length - 1 && 'pr-2',
                )}
                onClick={() => handleClick(star)}
                onMouseEnter={() => handleMouseEnter(star)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  tabIndex={readOnly || disabled ? -1 : 0}
                  aria-label={t('common:rating.starLabel', { star })}
                  disabled={disabled}
                  className={cn(
                    'p-0 bg-transparent border-none outline-none cursor-pointer transition-transform',
                    (readOnly || disabled) && 'cursor-default',
                  )}
                  style={{ width: size, height: size }}
                >
                  {star <= displayValue ? (
                    // 选中状态 SVG
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={size}
                      height={size}
                      viewBox="0 0 17.1904 16.4375"
                    >
                      <path
                        d="M8.41542 0.196285L10.7194 4.86429L15.8854 5.61429C16.0235 5.64176 16.1152 5.72214 16.1605 5.85543C16.2058 5.98873 16.1821 6.10834 16.0894 6.21429L12.3454 9.87429L13.2274 15.0103C13.2384 15.0784 13.2307 15.1443 13.2044 15.208C13.1781 15.2717 13.1371 15.3239 13.0813 15.3644C13.0256 15.405 12.9633 15.4279 12.8946 15.4333C12.8259 15.4387 12.7608 15.4257 12.6994 15.3943L8.09142 12.9943L3.48341 15.3943C3.42203 15.4257 3.35697 15.4387 3.28824 15.4333C3.2195 15.4279 3.15726 15.405 3.1015 15.3644C3.04574 15.3239 3.00471 15.2717 2.97842 15.208C2.95213 15.1443 2.94446 15.0784 2.95542 15.0103L3.82542 9.87429L0.0934148 6.23829C0.00069806 6.13234 -0.0230007 6.01272 0.0223183 5.87943C0.0676374 5.74614 0.159336 5.66576 0.297415 5.63829L5.45142 4.88829L7.75542 0.220286C7.78348 0.155627 7.82663 0.103431 7.88485 0.0636992C7.94308 0.0239677 8.00741 0.00282105 8.07785 0.000259243C8.14829 -0.00230209 8.21399 0.0141161 8.27495 0.0495139C8.33591 0.0849117 8.38273 0.133835 8.41542 0.196285Z"
                        fill-rule="nonzero"
                        transform="matrix(1 0 0 1 0.503906 0.5)"
                        fill="rgb(255, 159, 72)"
                      />
                      <path
                        d="M8.86378 -0.0250132L11.1678 4.64299L10.7194 4.86429L10.7913 4.36947L15.9702 5.12135L15.983 5.1239Q16.4731 5.22141 16.6339 5.69448Q16.7947 6.16756 16.4657 6.54357L16.4529 6.55822L12.6949 10.2318L12.3454 9.87429L12.8382 9.78966L13.7202 14.9257Q13.7599 15.1725 13.6666 15.3987Q13.5733 15.6249 13.3754 15.7688Q13.1775 15.9127 12.9336 15.9318Q12.6896 15.9509 12.4718 15.8394L7.86045 13.4377L8.09142 12.9943L8.32238 13.4377L3.71438 15.8377Q3.49322 15.9509 3.24926 15.9318Q3.00531 15.9127 2.80741 15.7688Q2.60951 15.6249 2.5162 15.3987Q2.42289 15.1725 2.46176 14.9309L3.33244 9.79078L3.82542 9.87429L3.4765 10.2324L-0.269748 6.58254L-0.282846 6.56757Q-0.611913 6.19156 -0.451068 5.71848Q-0.290222 5.24541 0.199844 5.1479L0.212572 5.14537L5.37942 4.3935L5.45142 4.88829L5.00306 4.66699L7.30706 -0.00101241L7.75542 0.220286L7.29676 0.0211933Q7.39638 -0.208292 7.60303 -0.349305Q7.80967 -0.490318 8.05968 -0.49941Q8.30969 -0.508501 8.52604 -0.382868Q8.74239 -0.257233 8.8584 -0.0355846L8.86115 -0.0303308L8.86378 -0.0250132ZM7.96706 0.417584L8.41542 0.196285L7.97243 0.428155Q7.99039 0.462457 8.02386 0.481896Q8.05733 0.501336 8.09602 0.499929Q8.13471 0.498522 8.16668 0.476703Q8.19866 0.454886 8.21407 0.419379L8.20919 0.430608L5.78347 5.34523L0.369415 6.13308L0.297415 5.63829L0.394985 6.12867Q0.470816 6.11359 0.495705 6.04038Q0.520593 5.96718 0.469676 5.909L0.0934148 6.23829L0.442332 5.88016L4.3622 9.69919L3.44907 15.0897Q3.45508 15.0523 3.44064 15.0173Q3.4262 14.9823 3.39558 14.9601Q3.36496 14.9378 3.32722 14.9348Q3.28946 14.9319 3.25245 14.9508L8.09142 12.4305L12.9271 14.9491Q12.8934 14.9319 12.8556 14.9348Q12.8179 14.9378 12.7873 14.9601Q12.7566 14.9823 12.7422 15.0173Q12.7278 15.0523 12.7346 15.0949L11.8082 9.70022L15.7399 5.85675L16.0894 6.21429L15.7132 5.885Q15.6622 5.94318 15.6871 6.01639Q15.712 6.08959 15.7878 6.10467L15.8854 5.61429L15.8136 6.1091L10.3874 5.32133L7.96706 0.417584Z"
                        fill-rule="nonzero"
                        transform="matrix(1 0 0 1 0.503906 0.5)"
                        fill="rgb(255, 159, 72)"
                      />
                    </svg>
                  ) : (
                    // 未选中状态 SVG
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={size}
                      height={size}
                      viewBox="0 0 17.1904 16.4375"
                    >
                      <path
                        d="M8.41542 0.196285L10.7194 4.86429L15.8854 5.61429C16.0235 5.64176 16.1152 5.72214 16.1605 5.85543C16.2058 5.98873 16.1821 6.10834 16.0894 6.21429L12.3454 9.87429L13.2274 15.0103C13.2384 15.0784 13.2307 15.1443 13.2044 15.208C13.1781 15.2717 13.1371 15.3239 13.0813 15.3644C13.0256 15.405 12.9633 15.4279 12.8946 15.4333C12.8259 15.4387 12.7608 15.4257 12.6994 15.3943L8.09142 12.9943L3.48341 15.3943C3.42203 15.4257 3.35697 15.4387 3.28824 15.4333C3.2195 15.4279 3.15726 15.405 3.1015 15.3644C3.04574 15.3239 3.00471 15.2717 2.97842 15.208C2.95213 15.1443 2.94446 15.0784 2.95542 15.0103L3.82542 9.87429L0.0934148 6.23829C0.00069806 6.13234 -0.0230007 6.01272 0.0223183 5.87943C0.0676374 5.74614 0.159336 5.66576 0.297415 5.63829L5.45142 4.88829L7.75542 0.220286C7.78348 0.155627 7.82663 0.103431 7.88485 0.0636992C7.94308 0.0239677 8.00741 0.00282105 8.07785 0.000259243C8.14829 -0.00230209 8.21399 0.0141161 8.27495 0.0495139C8.33591 0.0849117 8.38273 0.133835 8.41542 0.196285Z"
                        fill-rule="nonzero"
                        transform="matrix(1 0 0 1 0.503906 0.5)"
                        fill={showLabels ? `rgb(255, 255, 255)` : '#F4F4F5'}
                      />
                      <path
                        d="M8.86378 -0.0250132L11.1678 4.64299L10.7194 4.86429L10.7913 4.36947L15.9702 5.12135L15.983 5.1239Q16.4731 5.22141 16.6339 5.69448Q16.7947 6.16756 16.4657 6.54357L16.4529 6.55822L12.6949 10.2318L12.3454 9.87429L12.8382 9.78966L13.7202 14.9257Q13.7599 15.1725 13.6666 15.3987Q13.5733 15.6249 13.3754 15.7688Q13.1775 15.9127 12.9336 15.9318Q12.6896 15.9509 12.4718 15.8394L7.86045 13.4377L8.09142 12.9943L8.32238 13.4377L3.71438 15.8377Q3.49322 15.9509 3.24926 15.9318Q3.00531 15.9127 2.80741 15.7688Q2.60951 15.6249 2.5162 15.3987Q2.42289 15.1725 2.46176 14.9309L3.33244 9.79078L3.82542 9.87429L3.4765 10.2324L-0.269748 6.58254L-0.282846 6.56757Q-0.611913 6.19156 -0.451068 5.71848Q-0.290222 5.24541 0.199844 5.1479L0.212572 5.14537L5.37942 4.3935L5.45142 4.88829L5.00306 4.66699L7.30706 -0.00101241L7.75542 0.220286L7.29676 0.0211933Q7.39638 -0.208292 7.60303 -0.349305Q7.80967 -0.490318 8.05968 -0.49941Q8.30969 -0.508501 8.52604 -0.382868Q8.74239 -0.257233 8.8584 -0.0355846L8.86115 -0.0303308L8.86378 -0.0250132ZM7.96706 0.417584L8.41542 0.196285L7.97243 0.428155Q7.99039 0.462457 8.02386 0.481896Q8.05733 0.501336 8.09602 0.499929Q8.13471 0.498522 8.16668 0.476703Q8.19866 0.454886 8.21407 0.419379L8.20919 0.430608L5.78347 5.34523L0.369415 6.13308L0.297415 5.63829L0.394985 6.12867Q0.470816 6.11359 0.495705 6.04038Q0.520593 5.96718 0.469676 5.909L0.0934148 6.23829L0.442332 5.88016L4.3622 9.69919L3.44907 15.0897Q3.45508 15.0523 3.44064 15.0173Q3.4262 14.9823 3.39558 14.9601Q3.36496 14.9378 3.32722 14.9348Q3.28946 14.9319 3.25245 14.9508L8.09142 12.4305L12.9271 14.9491Q12.8934 14.9319 12.8556 14.9348Q12.8179 14.9378 12.7873 14.9601Q12.7566 14.9823 12.7422 15.0173Q12.7278 15.0523 12.7346 15.0949L11.8082 9.70022L15.7399 5.85675L16.0894 6.21429L15.7132 5.885Q15.6622 5.94318 15.6871 6.01639Q15.712 6.08959 15.7878 6.10467L15.8854 5.61429L15.8136 6.1091L10.3874 5.32133L7.96706 0.417584Z"
                        fill-rule="nonzero"
                        transform="matrix(1 0 0 1 0.503906 0.5)"
                        fill="rgb(229, 231, 235)"
                      />
                    </svg>
                  )}
                </button>
                {showLabels && (
                  <span
                    className={cn(
                      'text-xs font-normal mt-2 whitespace-pre-line leading-tight transition-colors',
                      isActive ? 'text-[#3D3D3D]' : 'text-[#868687]',
                    )}
                    style={{ textAlign: 'center' }}
                  >
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
StarRating.displayName = 'StarRating';
